import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { FileDown, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import html2pdf from "html2pdf.js";
import writeXlsxFile from "write-excel-file";
import { mpApi, SERVICE_KEYS, serviceMeta, statusMeta, isOverdue, monthLabel, shiftMonth, fmtDay, pctOf } from "./mpShared";

const REPORTS = [
  ["clients", "Monthly Client Report", "Completed vs target per client and service"],
  ["services", "Service-wise Performance", "Each service across all clients"],
  ["employees", "Employee Performance", "Tasks assigned and completed per person"],
  ["overdue", "Overdue Tasks", "Everything past its due date"],
  ["tasks", "All Tasks", "Every deliverable this month"],
  ["history", "Client Work History", "Last 6 months for one client"],
];

const Z = { target: 0, total: 0, completed: 0, in_progress: 0, overdue: 0, pct: 0 };
const bs = (row, k) => (row && row.by_service && row.by_service[k]) || Z;

export default function ReportsTab({ month, businessId, overview, members, clients }) {
  const [type, setType] = useState("clients");
  const [tasks, setTasks] = useState(null);
  const [workload, setWorkload] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyClient, setHistoryClient] = useState("");
  const [exporting, setExporting] = useState("");
  const printRef = useRef(null);
  const clientName = useMemo(() => Object.fromEntries(clients.map((c) => [c.client_id, c.name])), [clients]);
  const memberName = useMemo(() => Object.fromEntries(members.map((m) => [m.user_id, m.name])), [members]);

  useEffect(() => {
    if ((type === "tasks" || type === "overdue") && !tasks) mpApi("/tasks", { params: { month, business_id: businessId || undefined } }).then((d) => setTasks(d.tasks)).catch((e) => toast.error(e.message));
    if (type === "employees" && !workload) mpApi("/workload", { params: { month, business_id: businessId || undefined } }).then(setWorkload).catch((e) => toast.error(e.message));
  }, [type, month, businessId, tasks, workload]);
  useEffect(() => { setTasks(null); setWorkload(null); setHistory(null); }, [month, businessId]);
  useEffect(() => { if (!historyClient && clients[0]) setHistoryClient(clients[0].client_id); }, [clients, historyClient]);

  useEffect(() => {
    if (type !== "history" || !historyClient) return;
    setHistory(null);
    const months = Array.from({ length: 6 }, (_, i) => shiftMonth(month, i - 5));
    Promise.all(months.map((m) => mpApi("/overview", { params: { month: m, business_id: businessId || undefined, all: "1" } })))
      .then((res) => setHistory(res.map((o, i) => ({ month: months[i], row: o.clients.find((c) => c.client_id === historyClient) }))))
      .catch((e) => toast.error(e.message));
  }, [type, historyClient, month, businessId]);

  // One table model per report → rendered on screen and exported identically.
  const table = useMemo(() => {
    if (type === "clients") {
      return {
        head: ["Client", ...SERVICE_KEYS.map((k) => serviceMeta(k).label), "Target", "Completed", "In Progress", "Pending", "Overdue", "Completion %"],
        rows: overview.clients.map((c) => [c.name, ...SERVICE_KEYS.map((k) => `${bs(c, k).completed}/${bs(c, k).target}`), c.target_total, c.totals.completed, c.totals.in_progress,
          Math.max(0, c.target_total - c.totals.completed - c.totals.in_progress), c.totals.overdue, `${c.totals.pct}%`]),
        foot: ["Total", ...SERVICE_KEYS.map((k) => `${(overview.service_totals[k] || Z).completed}/${(overview.service_totals[k] || Z).target}`), overview.totals.target, overview.totals.completed, overview.totals.in_progress,
          Math.max(0, overview.totals.target - overview.totals.completed - overview.totals.in_progress), overview.totals.overdue, `${overview.totals.pct}%`],
      };
    }
    if (type === "services") {
      return {
        head: ["Service", "Target", "Tasks created", "Completed", "In Progress", "Pending", "Overdue", "Completion %"],
        rows: SERVICE_KEYS.map((k) => { const s = overview.service_totals[k] || Z; return [serviceMeta(k).label, s.target, s.total, s.completed, s.in_progress, Math.max(0, s.target - s.completed - s.in_progress), s.overdue, `${s.pct}%`]; }),
      };
    }
    if (type === "employees") {
      if (!workload) return null;
      return {
        head: ["Employee", "Department", "Assigned", "Completed", "In Progress", "Pending", "Overdue", "Completion %"],
        rows: workload.members.filter((m) => m.counts.total > 0).map((m) => [m.name, m.department || "", m.counts.total, m.counts.completed, m.counts.in_progress, m.counts.pending, m.counts.overdue, `${m.pct}%`]),
      };
    }
    if (type === "tasks" || type === "overdue") {
      if (!tasks) return null;
      const list = type === "overdue" ? tasks.filter((t) => isOverdue(t)) : tasks;
      return {
        head: ["Due", "Client", "Service", "Task", "Assigned To", "Status", "Priority"],
        rows: list.map((t) => [fmtDay(t.due_date), clientName[t.client_id] || "", t.service ? serviceMeta(t.service).label : "—", t.title,
          (t.assigned_users || []).map((u) => memberName[u]).filter(Boolean).join(", ") || "Unassigned", statusMeta(t.status).label, t.priority || "medium"]),
      };
    }
    if (type === "history") {
      if (!history) return null;
      return {
        head: ["Month", ...SERVICE_KEYS.map((k) => serviceMeta(k).label), "Target", "Completed", "Completion %"],
        rows: history.map(({ month: m, row }) => row
          ? [monthLabel(m), ...SERVICE_KEYS.map((k) => `${bs(row, k).completed}/${bs(row, k).target}`), row.target_total, row.totals.completed, `${pctOf(row.totals.completed, row.target_total || row.totals.total)}%`]
          : [monthLabel(m), ...SERVICE_KEYS.map(() => "—"), 0, 0, "—"]),
      };
    }
    return null;
  }, [type, overview, workload, tasks, history, clientName, memberName]);

  const title = `${REPORTS.find((r) => r[0] === type)[1]}${type === "history" ? ` — ${clientName[historyClient] || ""}` : ""} — ${monthLabel(month)}`;
  const fileBase = `work-planner-${type}-${month}`;

  const exportCsv = () => {
    const esc = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [table.head, ...table.rows, ...(table.foot ? [table.foot] : [])].map((r) => r.map(esc).join(","));
    const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${fileBase}.csv`; a.click(); URL.revokeObjectURL(a.href);
  };
  const exportXlsx = async () => {
    setExporting("xlsx");
    try {
      const cell = (v, bold) => ({ value: typeof v === "number" ? v : String(v ?? ""), type: typeof v === "number" ? Number : String, fontWeight: bold ? "bold" : undefined });
      const width = table.head.length;
      const pad = (row) => [...row, ...Array(Math.max(0, width - row.length)).fill(null)];
      const data = [
        pad([{ value: title, type: String, fontWeight: "bold" }]),
        pad([]),
        table.head.map((h) => cell(h, true)),
        ...table.rows.map((r) => r.map((v) => cell(v))),
        ...(table.foot ? [table.foot.map((v) => cell(v, true))] : []),
      ];
      await writeXlsxFile(data, { fileName: `${fileBase}.xlsx`, columns: table.head.map((h, i) => ({ width: i === 0 ? 30 : Math.max(10, h.length + 2) })) });
    } catch (e) { toast.error("Could not create the Excel file"); } finally { setExporting(""); }
  };
  const exportPdf = async () => {
    if (!printRef.current) return;
    setExporting("pdf");
    try {
      await html2pdf().set({ margin: 8, filename: `${fileBase}.pdf`, image: { type: "jpeg", quality: 0.95 }, html2canvas: { scale: 2 }, jsPDF: { unit: "mm", format: "a4", orientation: table.head.length > 8 ? "landscape" : "portrait" } }).from(printRef.current).save();
    } catch (e) { toast.error("Could not create the PDF"); } finally { setExporting(""); }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
      <div className="space-y-1.5">
        {REPORTS.map(([k, label, hint]) => (
          <button key={k} onClick={() => setType(k)} data-testid={`mp-report-${k}`}
            className={`w-full rounded-xl border p-3 text-left transition ${type === k ? "border-indigo-400 bg-indigo-50/60" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
            <div className="text-sm font-medium text-slate-900">{label}</div>
            <div className="text-xs text-slate-500">{hint}</div>
          </button>
        ))}
      </div>

      <div className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {type === "history" && (
              <select value={historyClient} onChange={(e) => setHistoryClient(e.target.value)} className="h-9 rounded-lg border border-slate-200 px-2 text-sm">
                {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
              </select>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={exportPdf} disabled={!table || !!exporting} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              {exporting === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5 text-rose-600" />} PDF
            </button>
            <button onClick={exportXlsx} disabled={!table || !!exporting} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              {exporting === "xlsx" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5 text-green-600" />} Excel
            </button>
            <button onClick={exportCsv} disabled={!table} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              <FileDown className="h-3.5 w-3.5 text-slate-500" /> CSV
            </button>
          </div>
        </div>

        <div ref={printRef} className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3">
            <h2 className="text-base font-semibold text-slate-900">{title}</h2>
            <p className="text-xs text-slate-500">Digital Nexus Media · generated {new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</p>
          </div>
          {!table ? <div className="py-12 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div>
            : table.rows.length === 0 ? <div className="py-12 text-center text-sm text-slate-500">Nothing to report for {monthLabel(month)}.</div>
            : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-left font-semibold text-slate-600">
                    <tr>{table.head.map((h, i) => <th key={h} className={`px-2 py-2 ${i > 0 ? "text-right" : ""}`}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {table.rows.map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">{r.map((v, j) => <td key={j} className={`px-2 py-1.5 text-slate-700 ${j > 0 ? "text-right tabular-nums" : "font-medium text-slate-900"}`}>{v}</td>)}</tr>
                    ))}
                  </tbody>
                  {table.foot && <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold text-slate-800"><tr>{table.foot.map((v, j) => <td key={j} className={`px-2 py-2 ${j > 0 ? "text-right tabular-nums" : ""}`}>{v}</td>)}</tr></tfoot>}
                </table>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
